import React, { useState, useEffect } from 'react';
import { 
  Scissors, Calendar, ChevronDown, Sparkles, User, Home, 
  ShoppingBag, Film, MessageSquare, MapPin, Tag 
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { salonInfo } from '../data/salonInfo';
import { getSalonCurrentStatus } from '../utils/dateUtils';
import { getActiveSpecialDiscounts } from '../utils/storage';

export default function Navbar({ 
  onOpenBooking, 
  onOpenAdmin, 
  onOpenAuth, 
  onOpenProfile, 
  onOpenSpecialDays,
  currentPage = 'home',
  onNavigate
}) {
  const { lang, setLang, t, languages } = useLanguage();
  const { currentUser, loyaltyInfo } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [salonStatus, setSalonStatus] = useState(getSalonCurrentStatus(lang));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    setSalonStatus(getSalonCurrentStatus(lang));
    const interval = setInterval(() => {
      setSalonStatus(getSalonCurrentStatus(lang));
    }, 60000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, [lang]);

  const activeSpecialDiscounts = getActiveSpecialDiscounts();
  const activeSpecial = activeSpecialDiscounts.length > 0 ? activeSpecialDiscounts[0] : null;

  const navLinks = [
    { id: 'home', label: lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home', href: '#home', icon: Home },
    { id: 'services', label: t.nav.services, href: '#hizmetler', icon: Scissors },
    { id: 'hairpiece', label: t.nav.hairpiece, href: '#haarwerk', icon: Sparkles, isSpecial: true },
    { id: 'products', label: t.nav.products || (lang === 'nl' ? 'Producten' : lang === 'tr' ? 'Ürünler' : 'Products'), href: '#producten', icon: ShoppingBag },
    { id: 'gallery', label: t.nav.gallery, href: '#galeri', icon: Film },
    { id: 'reviews', label: t.nav.reviews, href: '#yorumlar', icon: MessageSquare },
    { id: 'contact', label: t.nav.contact, href: '#iletisim', icon: MapPin },
  ];

  const currentLanguage = languages.find(l => l.code === lang) || languages[0];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 shadow-md">
      
      {/* ================= TIER 1: BRAND & PRIMARY ACTIONS ROW ================= */}
      <div className={`w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/98 backdrop-blur-md border-b border-slate-200/90 py-2 sm:py-2.5' 
          : 'bg-white/95 backdrop-blur-sm border-b border-slate-200/80 py-2 sm:py-3'
      }`}>
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            
            {/* Left: Brand Logo & Scissors button (Direct Admin Access) */}
            <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
              <button
                onClick={onOpenAdmin}
                type="button"
                title={lang === 'nl' ? 'Aura Hair Beheerder Inloggen (Admin Panel)' : lang === 'tr' ? 'Aura Hair Yönetici Girişi (Admin Panel)' : 'Aura Hair Admin Login'}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 flex items-center justify-center shadow-md shadow-amber-500/25 hover:scale-110 active:scale-95 transition-all border border-amber-400/40 shrink-0 cursor-pointer group"
              >
                <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-white stroke-[2.5] group-hover:rotate-12 transition-transform" />
              </button>

              <a 
                href="#home" 
                onClick={(e) => {
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate('home');
                  }
                }}
                className="flex flex-col cursor-pointer"
              >
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="font-serif text-base sm:text-2xl font-black tracking-tight text-slate-900 hover:text-amber-600 transition-colors leading-none whitespace-nowrap">
                    AURA<span className="text-amber-600"> HAIR</span>
                  </span>
                  <span 
                    className={`inline-block w-2 h-2 rounded-full shrink-0 ${salonStatus.isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} 
                    title={salonStatus.statusText} 
                  />
                </div>
                <span className="text-[9px] sm:text-[10px] tracking-wider uppercase font-bold text-amber-700 font-sans mt-0.5 whitespace-nowrap">
                  ROTTERDAM &bull; KAPSALON
                </span>
              </a>
            </div>

            {/* Right: Actions (Language, Auth, Specials, Booking CTA) - ALWAYS VISIBLE, NO HAMBURGER */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              
              {/* Language Selector Dropdown */}
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center gap-1 px-2 py-1.5 sm:py-2 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-xs font-bold text-slate-800 transition-colors shadow-xs shrink-0 cursor-pointer"
                  title="Taal / Language"
                >
                  <span className="text-sm">{currentLanguage.flag}</span>
                  <span className="hidden sm:inline uppercase font-extrabold text-[11px]">{currentLanguage.code}</span>
                  <ChevronDown className="w-3 h-3 text-slate-500" />
                </button>

                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-36 bg-white border border-slate-200 rounded-xl shadow-xl py-1 z-50 animate-fade-in">
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => {
                          setLang(l.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer ${
                          lang === l.code ? 'bg-amber-50 text-amber-800 font-extrabold' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <span className="text-sm">{l.flag}</span>
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer Auth / Profile Button - ALWAYS VISIBLE ON MOBILE & DESKTOP */}
              {currentUser ? (
                <button
                  type="button"
                  onClick={onOpenProfile}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 text-xs font-bold rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 transition-all shadow-xs shrink-0 whitespace-nowrap cursor-pointer"
                  title={lang === 'nl' ? 'Klantprofiel & Spaarkaart' : lang === 'tr' ? 'Müşteri Profili & Sadakat Kartı' : 'Customer Profile & Loyalty Card'}
                >
                  <div className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-black shrink-0">
                    {currentUser.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline max-w-[80px] truncate">{currentUser.name}</span>
                  <span className="px-1.5 py-0.2 rounded bg-amber-500 text-white text-[10px] font-mono font-black shrink-0">
                    {loyaltyInfo.stamps}/10
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onOpenAuth}
                  className="flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 sm:py-2 text-xs font-bold rounded-xl text-slate-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 transition-all shadow-xs shrink-0 whitespace-nowrap cursor-pointer"
                  title={t.nav.loginRegister}
                >
                  <User className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="whitespace-nowrap font-bold">{lang === 'nl' ? 'Inloggen' : lang === 'tr' ? 'Giriş' : 'Log In'}</span>
                </button>
              )}

              {/* 📱 Direct Mobile App Link */}
              <a
                href="/?app=1"
                className="flex items-center gap-1 px-2 py-1.5 sm:py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-black text-xs transition-all shadow-xs shrink-0 whitespace-nowrap"
                title="Aura Hair Mobile Booking App"
              >
                <span>📱</span>
                <span className="font-extrabold text-[11px]">APP</span>
              </a>

              {/* 🎁 2026 Feestdagen Deals Knop (%10) */}
              <button
                type="button"
                onClick={onOpenSpecialDays}
                className="hidden sm:flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl bg-purple-50 hover:bg-purple-100 border border-purple-300 text-purple-900 text-xs font-black transition-all shadow-xs shrink-0 whitespace-nowrap cursor-pointer group"
                title={lang === 'nl' ? 'Bekijk speciale 2026 feestdagen acties' : lang === 'tr' ? 'Özel gün indirimleri takvimini gör' : 'View special 2026 holiday deals'}
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500 group-hover:rotate-12 transition-transform shrink-0" />
                <span className="hidden lg:inline">{lang === 'nl' ? 'Feestdagen Deals' : lang === 'tr' ? 'Özel Günler' : 'Holiday Deals'}</span>
                <span className="px-1.5 py-0.5 rounded bg-purple-600 text-white text-[10px] font-mono font-black shrink-0">
                  %10
                </span>
              </button>

              {/* Direct Booking CTA Button (Randevu Al) */}
              <button
                type="button"
                onClick={onOpenBooking}
                className="btn-gold-sheen flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-black text-xs uppercase tracking-wider shadow-md shadow-amber-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all shrink-0 whitespace-nowrap cursor-pointer"
              >
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span>{t.nav.bookNow}</span>
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ================= TIER 2: PERSISTENT MULTI-PAGE NAVIGATION BAR (NO HAMBURGER!) ================= */}
      <nav 
        aria-label="Salon Departments Navigation" 
        className="w-full bg-slate-950 border-b border-amber-500/30 text-white shadow-lg overflow-hidden"
      >
        <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-start sm:justify-center overflow-x-auto no-scrollbar scroll-smooth py-1.5 sm:py-2 gap-1 sm:gap-2">
            {navLinks.map((link) => {
              const isActive = currentPage === link.id;
              const Icon = link.icon;

              return (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => {
                    if (onNavigate) {
                      onNavigate(link.id);
                    }
                  }}
                  className={`py-1.5 px-3 sm:px-3.5 rounded-xl text-xs sm:text-xs font-bold transition-all duration-200 flex items-center gap-1.5 shrink-0 whitespace-nowrap cursor-pointer ${
                    isActive
                      ? 'bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/30 scale-[1.03]'
                      : link.isSpecial
                        ? 'bg-emerald-950/80 text-emerald-300 hover:text-white hover:bg-emerald-900 border border-emerald-500/40'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800/90 border border-slate-800/80'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${
                    isActive 
                      ? 'text-slate-950 stroke-[2.5]' 
                      : link.isSpecial 
                        ? 'text-emerald-400' 
                        : 'text-amber-400'
                  }`} />
                  <span>{link.label}</span>
                  {link.isSpecial && !isActive && (
                    <span className="hidden sm:inline px-1 py-0.2 rounded text-[9px] bg-emerald-500 text-slate-950 font-black">
                      100%
                    </span>
                  )}
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-950 animate-pulse ml-0.5" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

    </header>
  );
}
